// 📄 components/layout/Navbar.tsx
// Navbar principal reutilizable: la usa tanto OperativeApp (páginas
// públicas/basadas en estado) como las rutas nuevas de Proyectos/Clientes,
// para que el usuario nunca quede sin navegación visible.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useUnreadChats } from '../../hooks/useUnreadChats.ts';
import TareasBell from './TareasBell.tsx';
import GlobalSearchModal from './GlobalSearchModal.tsx';
import UserMenu from './UserMenu.tsx';
import ThemeToggle from './ThemeToggle.tsx';
import OrgSwitcher from './OrgSwitcher.tsx';
import OneHubLogo from '../ui/OneHubLogo.tsx';

interface NavbarProps {
  activePage?: 'home' | 'clientes' | 'proyectos' | 'mensajes' | 'soporte' | 'ayuda';
  onHomeClick?: () => void;
  // OperativeApp pasa su propio requireAuth (con modal de login inline)
  // porque Home/Ariba/BTP son públicos. Las rutas nuevas ya están detrás
  // de RequireAuth a nivel de ruta, así que ahí no hace falta gatear nada.
  requireAuth?: (callback: () => void) => void;
}

export default function Navbar({ activePage, onHomeClick, requireAuth }: NavbarProps) {
  const { user, profile, esClienteExterno } = useAuth();
  const navigate = useNavigate();
  const guard = requireAuth || ((callback: () => void) => callback());
  const chatsNoLeidos = useUnreadChats();

  return (
    <nav className="sticky top-0 z-40 bg-gray-800 p-4 flex justify-between items-center shadow-md">
      <div className="flex items-center gap-2 text-xl font-bold text-blue-400">
        <OneHubLogo size={28} />
        OneHub Platform
      </div>

      <ul className="flex gap-6 text-sm md:text-base">
        <li
          onClick={() => (onHomeClick ? onHomeClick() : navigate('/'))}
          className={`cursor-pointer pb-1 border-b-2 transition-colors ${
            activePage === 'home' ? 'text-blue-400 border-blue-400' : 'border-transparent hover:text-blue-400'
          }`}
        >
          Inicio
        </li>

        {!esClienteExterno && (
          <li
            onClick={() => guard(() => navigate('/clientes'))}
            className={`cursor-pointer pb-1 border-b-2 transition-colors ${
              activePage === 'clientes' ? 'text-blue-400 border-blue-400' : 'border-transparent hover:text-blue-400'
            }`}
          >
            Clientes
          </li>
        )}

        <li
          onClick={() => guard(() => navigate('/proyectos'))}
          className={`cursor-pointer pb-1 border-b-2 transition-colors ${
            activePage === 'proyectos' ? 'text-blue-400 border-blue-400' : 'border-transparent hover:text-blue-400'
          }`}
        >
          Mis Proyectos
        </li>

        {user && (
          <li
            onClick={() => guard(() => navigate('/ayuda'))}
            className={`cursor-pointer pb-1 border-b-2 transition-colors ${
              activePage === 'ayuda' ? 'text-blue-400 border-blue-400' : 'border-transparent hover:text-blue-400'
            }`}
          >
            Ayuda
          </li>
        )}

        {profile?.role === 'admin' && !esClienteExterno && (
          <li
            onClick={() => guard(() => navigate('/soporte'))}
            className={`cursor-pointer pb-1 border-b-2 transition-colors ${
              activePage === 'soporte' ? 'text-blue-400 border-blue-400' : 'border-transparent hover:text-blue-400'
            }`}
          >
            Soporte
          </li>
        )}
      </ul>

      <div className="flex items-center gap-3">
        <ThemeToggle />
        {user ? (
          <>
            <button
              onClick={() => guard(() => navigate('/mensajes'))}
              title="Mensajes"
              className={`relative p-1.5 rounded-lg transition-colors ${
                activePage === 'mensajes' ? 'text-blue-400' : 'text-gray-300 hover:text-blue-400'
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              {chatsNoLeidos > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                  {chatsNoLeidos > 9 ? '9+' : chatsNoLeidos}
                </span>
              )}
            </button>
            <GlobalSearchModal />
            <TareasBell />
            <OrgSwitcher />
            <UserMenu />
          </>
        ) : (
          <button
            onClick={() => guard(() => {})}
            className="bg-blue-500 px-4 py-1 rounded-md text-sm"
          >
            Iniciar Sesión
          </button>
        )}
      </div>
    </nav>
  );
}
